import { MergeFieldData } from '@fluentsms/fluent-client-documents';
import { MergeField, MergeFieldFormatKeys } from '@fluentsms/merge';
import Chance from 'chance';
import { IntrospectionQuery } from 'graphql';

type GQLMergeField = Partial<MergeFieldData> & Pick<MergeFieldData, 'label' | 'mergeKey' | 'dataType' | 'domain'>;

const chance = new Chance();

export const mockSchema: IntrospectionQuery = {
  __schema: {
    directives: [],
    queryType: {
      name: 'Query',
      kind: 'OBJECT',
    },
    mutationType: {
      name: 'Mutation',
      kind: 'OBJECT',
    },
    subscriptionType: null,
    types: [],
  },
};

export const mergeFieldDataFields: MergeField[] = [
  {
    dataSource: 'mergeField1',
    fieldLabel: 'mergeField1',
    originData: 'mergeField1',
    originalFieldValue: 'mergeField1',
    fieldValue: chance.word(),
    isOverridden: false,
    updatedTimestamp: '',
    rawValue: 'mergeField1',
  },
  {
    dataSource: 'mergeField2',
    fieldLabel: 'mergeField2',
    originData: 'mergeField2',
    originalFieldValue: 'mergeField2',
    fieldValue: chance.word(),
    isOverridden: true,
    updatedTimestamp: '',
    rawValue: 'mergeField2',
  },
];

/*
 * Array Index Reference:
 * 0-3: Currency fields
 * 4-5: Time
 * 6-7: Date
 * 8: Number
 * 9: Percent
 * 10: Dropdown
 * 11: Text
 * 12: Boolean
 * 13: State
 */
export const mockMergeFields: GQLMergeField[] = [
  { label: 'Title Field 1', mergeKey: 'title.sub1', dataType: MergeFieldFormatKeys.CURRENCY, domain: 'Title' },
  { label: 'Title Field 2', mergeKey: 'title.sub2', dataType: MergeFieldFormatKeys.CURRENCY, domain: 'Title' },
  { label: 'Time Field 1', mergeKey: 'title.sub3', dataType: MergeFieldFormatKeys.TIME, domain: 'Title' },
  { label: 'Time Field 2', mergeKey: 'title.sub4', dataType: MergeFieldFormatKeys.TIME, domain: 'Title' },
  { label: 'Date Field 1', mergeKey: 'date.sub5', dataType: MergeFieldFormatKeys.DATE, domain: 'Title' },
  { label: 'Date Field 2', mergeKey: 'date.sub6', dataType: MergeFieldFormatKeys.DATE, domain: 'Title' },
  { label: 'Boolean Field', mergeKey: 'title.boolean1', dataType: MergeFieldFormatKeys.BOOLEAN, domain: 'Title' },
  { label: 'State Field', mergeKey: 'title.state', dataType: MergeFieldFormatKeys.STATE, domain: 'Title' },
  { label: 'Number field', mergeKey: 'order.number1', dataType: MergeFieldFormatKeys.NUMBER, domain: 'Order' },
  { label: 'Percent Field', mergeKey: 'order.percent1', dataType: MergeFieldFormatKeys.PERCENT, domain: 'Order' },
  { label: 'Dropdown Field', mergeKey: 'order.dropdown1', dataType: MergeFieldFormatKeys.TEXT, domain: 'Order' },
  { label: 'Text Field', mergeKey: 'order.text1', dataType: MergeFieldFormatKeys.TEXT, domain: 'Order' },
  {
    label: 'Settlement Field 1',
    mergeKey: 'settlement.sub1',
    dataType: MergeFieldFormatKeys.CURRENCY,
    domain: 'Settlement',
  },
  {
    label: 'Settlement Field 2',
    mergeKey: 'settlement.sub2',
    dataType: MergeFieldFormatKeys.CURRENCY,
    domain: 'Settlement',
  },
];

export const mockMergeFieldsForSorting: GQLMergeField[] = [
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.DATE,
    defaultFormat: 'Date',
    domain: 'Order',
    label: 'Settlement Date',
    mergeKey: 'order.main.dateSettlement',
  },
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.CURRENCY,
    defaultFormat: 'Currency',
    domain: 'Order',
    label: 'Sale Amount',
    mergeKey: 'order.main.amountSale',
  },
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.DATE,
    defaultFormat: 'Date',
    domain: 'Order',
    label: 'Funding Date',
    mergeKey: 'order.main.dateFunding',
  },
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.TEXT,
    defaultFormat: 'String',
    domain: 'Order',
    label: 'Alta ID',
    mergeKey: 'order.main.titleCompany.nmlsId',
  },
  {
    category: 'Buyers',
    dataType: MergeFieldFormatKeys.TEXT,
    defaultFormat: 'String',
    domain: 'Order',
    label: 'Buyer All Names',
    mergeKey: 'mergeData.order.buyersAllNames',
  },
  {
    category: 'Title',
    dataType: MergeFieldFormatKeys.TIME,
    defaultFormat: 'Time',
    domain: 'Title',
    label: 'Effective Time',
    mergeKey: 'TitleGeneral.timeEffective',
  },
  {
    category: 'Title',
    dataType: MergeFieldFormatKeys.DATE,
    defaultFormat: 'Date',
    domain: 'Title',
    label: 'Effective Date',
    mergeKey: 'TitleGeneral.dateEffective',
  },
];

export const mockMergeFieldsSorted: GQLMergeField[] = [
  {
    category: 'Buyers',
    dataType: MergeFieldFormatKeys.TEXT,
    defaultFormat: 'String',
    domain: 'Order',
    label: 'Buyer All Names',
    mergeKey: 'mergeData.order.buyersAllNames',
  },
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.TEXT,
    defaultFormat: 'String',
    domain: 'Order',
    label: 'Alta ID',
    mergeKey: 'order.main.titleCompany.nmlsId',
  },
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.DATE,
    defaultFormat: 'Date',
    domain: 'Order',
    label: 'Funding Date',
    mergeKey: 'order.main.dateFunding',
  },
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.CURRENCY,
    defaultFormat: 'Currency',
    domain: 'Order',
    label: 'Sale Amount',
    mergeKey: 'order.main.amountSale',
  },
  {
    category: 'Order',
    dataType: MergeFieldFormatKeys.DATE,
    defaultFormat: 'Date',
    domain: 'Order',
    label: 'Settlement Date',
    mergeKey: 'order.main.dateSettlement',
  },
  {
    category: 'Title',
    dataType: MergeFieldFormatKeys.DATE,
    defaultFormat: 'Date',
    domain: 'Title',
    label: 'Effective Date',
    mergeKey: 'TitleGeneral.dateEffective',
  },
  {
    category: 'Title',
    dataType: MergeFieldFormatKeys.TIME,
    defaultFormat: 'Time',
    domain: 'Title',
    label: 'Effective Time',
    mergeKey: 'TitleGeneral.timeEffective',
  },
];

export const mockMergeFieldData = {
  titleGeneral: {
    seeExhibitA: false,
    includeMaritalStatus: true,
  },
  mergeData: {
    order: {
      header: {
        orderNumber: 'TSTA-04-TX-514',
      },
    },
  },
  order: {
    sellers: [
      {
        firstName: 'First Name',
      },
    ],
  },
};
